import {
  Autocomplete,
  CardActions,
  CardContent,
  CardHeader,
  Dialog,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Switch,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AMIService } from "../../../../service/AMI/AMIService";
import useToast from "../../../../hooks/useToast";
import useConfirm from "../../../../hooks/useConfirm";
import { TYPE_NOTIFICATION } from "../../shares/NotificationBull";
import AppButton from "../../shares/AppButton";
import StandardSkeletoon from "../../shares/loader/StandardSkeletoon";

const INIT_AMI_INFO = {
  libelle_type: "",
  cloture: "0",
  date_debut_publication: "",
  date_fin_publication: "",
  description: "",
  type: "externe",
  desc: "Volontariat",
};

const INIT_FORM_ERROR = {
  libele_error: null,
  cloture_error: null,
  date_debut_error: null,
  date_fin_error: null,
  description_error: null,
  type_error: null,
  desc_error: null,
};

const OPTION_CNADIDATURE = [
  { label: "AGEX", value: "AGEX" },
  { label: "Consultant Individuel", value: "Cons_indiv" },
  { label: "Consultant Interne", value: "Cons_int" },
  { label: "Consultant Externe", value: "Cons_ext" },
  { label: "EMS", value: "EMS" },
  { label: "Volontariat", value: "Volontariat" },
];

const AMIDialogForm = ({ open, onFormClose, onFormOpen, idAmi }) => {
  const [amiInfo, setAmiInfo] = useState(INIT_AMI_INFO);
  const [formErrors, setFormErrors] = useState(INIT_FORM_ERROR);
  const [submited, setSubmited] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const { Toast, showToast } = useToast();
  const navigate = useNavigate();
  const { DialogComponent, openConfirmDialog } = useConfirm();

  const handleFormClose = () => {
    onFormClose();
    setAmiInfo(INIT_AMI_INFO);
  };

  const handleChangeAMIInfo = (e) => {
    const { name, value } = e.target;
    setAmiInfo({ ...amiInfo, [name]: value });
  };

  const handleFetchAmiInfo = () => {
    setFormLoading(true);
    AMIService.findOneById(idAmi)
      .then((res) => {
        if (res && res.data) {
          setAmiInfo(res.data);
        }
      })
      .finally(() => {
        setFormLoading(false);
      });
  };

  const handleCheckDataValidation = () => {
    setFormErrors({
      libele_error: amiInfo.libelle_type ? null : "Libellé est obligatoire",
      cloture_error: amiInfo.cloture ? null : "Cloture est obligatoire",
      date_debut_error: amiInfo.date_debut_publication
        ? null
        : "Date de début est obligatoire",
      date_fin_error: amiInfo.date_fin_publication
        ? null
        : "Date de cloture est obligatoire",
      description_error: amiInfo.description
        ? null
        : "Description est obligatoire",
      type_error: amiInfo.type ? null : "Type est obligatoire",
      desc_error: amiInfo.desc ? null : "Option est obligatoire",
    });
    console.log("form error:", formErrors);

    return Object.values(formErrors).every((error) => error === null);
  };

  const handleSave = () => {
    if (idAmi) {
      handleUpdate();
    } else {
      handleInsert();
    }
  };
  const handleInsert = () => {
    setSubmited(true);
    setLoading(true);
    AMIService.insert(amiInfo)
      .then((res) => {
        if (res && res.success) {
          showToast(TYPE_NOTIFICATION.SUCCESS, res.message);
          handleFormClose();
        } else {
          showToast(TYPE_NOTIFICATION.ERROR, res.message);
        }
      })
      .finally(() => {
        setSubmited(false);
        setLoading(false);
      });
  };

  const handleUpdate = () => {
    setSubmited(true);
    setLoading(true);
    AMIService.update(amiInfo)
      .then((res) => {
        if (res && res.success) {
          showToast(TYPE_NOTIFICATION.SUCCESS, res.message);
          handleFormClose();
        } else {
          showToast(TYPE_NOTIFICATION.ERROR, res.message);
        }
      })
      .finally(() => {
        setSubmited(false);
        setLoading(false);
      });
  };

  const handleSubmit = () => {
    setSubmited(true);
    if (handleCheckDataValidation()) {
      handleSave();
    } else {
      showToast(TYPE_NOTIFICATION.ERROR, "Veuillez verifier les champs");
    }
  };

  useEffect(() => {
    if (amiInfo) {
      handleCheckDataValidation();
    }
  }, [amiInfo]);

  useEffect(() => {
    if (idAmi && open) {
      handleFetchAmiInfo();
    }
  }, [idAmi, open]);

  return (
    <>
      <DialogComponent />
      <Dialog open={open} onClose={handleFormClose} fullWidth maxWidth="sm">
        <Toast />
        <Grid>
          <StandardSkeletoon escalier={true} m={4} row={4} show={formLoading} />
        </Grid>
        <Paper
          sx={{ m: 3, display: formLoading ? "none" : "block" }}
          elevation={0}
        >
          <Grid>
            <CardHeader
              title={idAmi ? "Modifier AMI" : "Creer AMI"}
              action={
                <FormControlLabel
                  control={
                    <Switch
                      checked={amiInfo.cloture === "1"}
                      onChange={(e) =>
                        handleChangeAMIInfo({
                          target: {
                            name: "cloture",
                            value: e.target.checked ? "1" : "0",
                          },
                        })
                      }
                      name="cloture"
                    />
                  }
                  label={amiInfo.cloture === "0" ? "Ouvert" : "Cloturé"}
                />
              }
            />
            <CardContent>
              <Grid container>
                <FormControl fullWidth>
                  <label>Libellé</label>
                  <TextField
                    id="libelle_type"
                    name="libelle_type"
                    onChange={handleChangeAMIInfo}
                    size="small"
                    value={amiInfo.libelle_type || ""}
                    error={formErrors.libele_error && submited}
                    // error={formErrors.adresse_error && submited}
                    helperText={submited ? formErrors.libele_error : ""}
                    placeholder="Libellé"
                    fullWidth
                    aria-describedby="libelle_helper"
                  />
                </FormControl>
                <FormControl fullWidth>
                  <label>Reference</label>
                  <TextField
                    multiline
                    rows={4}
                    id="description"
                    name="description"
                    onChange={handleChangeAMIInfo}
                    size="small"
                    value={amiInfo.description || ""}
                    error={formErrors.description_error && submited}
                    // error={formErrors.adresse_error && submited}
                    helperText={submited ? formErrors.description_error : ""}
                    placeholder="Reference"
                    fullWidth
                    aria-describedby="reference_helper"
                  />
                </FormControl>
                <FormControl fullWidth>
                  <label>Date de début de publication</label>
                  <TextField
                    id="date_debut_publication"
                    name="date_debut_publication"
                    onChange={handleChangeAMIInfo}
                    size="small"
                    type="date"
                    value={(amiInfo.date_debut_publication || "").split(" ")[0]}
                    error={formErrors.date_debut_error && submited}
                    helperText={submited ? formErrors.date_debut_error : ""}
                    placeholder="date de début"
                    fullWidth
                    aria-describedby="date_debut_helper"
                  />
                </FormControl>
                <FormControl fullWidth>
                  <label>Date de fin de publication</label>
                  <TextField
                    id="date_fin_publication"
                    name="date_fin_publication"
                    onChange={handleChangeAMIInfo}
                    size="small"
                    type="date"
                    value={(amiInfo.date_fin_publication || "").split(" ")[0]}
                    error={formErrors.date_fin_error && submited}
                    helperText={submited ? formErrors.date_fin_error : ""}
                    placeholder="date de find"
                    fullWidth
                    aria-describedby="date_fin_helper"
                  />
                </FormControl>
                <FormControl
                  fullWidth
                  error={formErrors.type_error && submited}
                >
                  <label>Type AMI</label>
                  <RadioGroup
                    row
                    onChange={(e) => handleChangeAMIInfo(e)}
                    value={amiInfo.type || "interne"}
                    name="type"
                  >
                    <FormControlLabel
                      value="interne"
                      control={<Radio />}
                      label="Interne"
                      disabled
                    />
                    <FormControlLabel
                      value="externe"
                      control={<Radio />}
                      label="Externe"
                    />
                  </RadioGroup>
                  {formErrors.type_error && submited && (
                    <FormHelperText>{formErrors.type_error}</FormHelperText>
                  )}
                </FormControl>
                <FormControl fullWidth>
                  <label>Option </label>
                  <Autocomplete
                    size="small"
                    value={OPTION_CNADIDATURE.find(
                      (item) => item.value === amiInfo.desc
                    )}
                    options={OPTION_CNADIDATURE}
                    onChange={(e, v) => {
                      if (v)
                        handleChangeAMIInfo({
                          target: { name: "desc", value: v.value },
                        });
                      else
                        handleChangeAMIInfo({
                          target: { name: "desc", value: null },
                        });
                    }}
                    getOptionLabel={(option) => option.label ?? ""}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name="desc"
                        error={formErrors.desc_error && submited}
                        value={amiInfo.desc || ""}
                        helperText={formErrors.desc_error || ""}
                        label="Option"
                        placeholder="Option de candidature"
                      />
                    )}
                    sx={{ flexGrow: 1, mr: 0.5 }}
                  />
                </FormControl>
              </Grid>
            </CardContent>
            <CardActions sx={{ justifyContent: "flex-end" }}>
              <AppButton
                loading={loading}
                text={"Enregistrer"}
                variant={"contained"}
                size={"medium"}
                onClick={(e) => handleSubmit()}
              />
              <AppButton
                text={"Fermer"}
                size={"medium"}
                variant={"outlined"}
                onClick={(e) => handleFormClose()}
              />
            </CardActions>
          </Grid>
        </Paper>
      </Dialog>
    </>
  );
};

export default AMIDialogForm;
